/**
 * DiscoveryMap - Simple map for showing destination during discovery flow
 * 
 * Features:
 * - Single destination pin (when destination is known)
 * - Ghost/faded pin (when AI suggests a location)
 * - Empty state with subtle hint
 */

import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { logger } from '../../utils/logger';
import './DiscoveryMap.css';

// Fix for default marker icons in Leaflet with bundlers
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

// Orange marker for confirmed destination
const destinationIcon = new L.Icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(`
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="48" viewBox="0 0 32 48">
            <path fill="#f97316" stroke="#ea580c" stroke-width="1.5" d="M16 0C7.2 0 0 7.2 0 16c0 16 16 32 16 32s16-16 16-32C32 7.2 24.8 0 16 0z"/>
            <circle fill="#fff" cx="16" cy="16" r="6"/>
        </svg>
    `),
    iconSize: [32, 48],
    iconAnchor: [16, 48],
    popupAnchor: [0, -40],
});

// Ghost marker for suggested destination
const ghostIcon = new L.Icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(`
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="48" viewBox="0 0 32 48">
            <path fill="#f9731680" stroke="#ea580c40" stroke-width="1.5" d="M16 0C7.2 0 0 7.2 0 16c0 16 16 32 16 32s16-16 16-32C32 7.2 24.8 0 16 0z"/>
            <circle fill="#ffffff80" cx="16" cy="16" r="6"/>
        </svg>
    `),
    iconSize: [32, 48],
    iconAnchor: [16, 48],
    popupAnchor: [0, -40],
});

export interface DestinationLocation {
    name: string;
    lat: number;
    lng: number;
    isSuggested?: boolean; // true = ghost/faded pin
}

interface DiscoveryMapProps {
    destination?: DestinationLocation | null;
    onMapReady?: () => void;
}

export default function DiscoveryMap({ destination, onMapReady }: DiscoveryMapProps) {
    const mapRef = useRef<L.Map | null>(null);
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const markerRef = useRef<L.Marker | null>(null);

    // Initialize map
    useEffect(() => {
        if (!mapContainerRef.current || mapRef.current) return;

        const map = L.map(mapContainerRef.current, {
            zoomControl: false, // Cleaner look
            attributionControl: false,
        }).setView([40, 10], 3); // Default: Europe-ish view

        // Light, subtle tile layer
        L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
            attribution: '&copy; <a href="https://carto.com/">CARTO</a>',
            maxZoom: 18,
        }).addTo(map);

        // Add zoom control in bottom right
        L.control.zoom({ position: 'bottomright' }).addTo(map);

        mapRef.current = map;
        onMapReady?.();

        return () => {
            map.remove();
            mapRef.current = null;
        };
    }, [onMapReady]);

    // Update marker when destination changes
    useEffect(() => {
        if (!mapRef.current) return;

        // Remove existing marker
        if (markerRef.current) {
            markerRef.current.remove();
            markerRef.current = null;
        }

        // Validate destination has valid coordinates
        if (destination &&
            typeof destination.lat === 'number' &&
            typeof destination.lng === 'number' &&
            !isNaN(destination.lat) &&
            !isNaN(destination.lng) &&
            destination.lat >= -90 && destination.lat <= 90 &&
            destination.lng >= -180 && destination.lng <= 180) {

            try {
                const icon = destination.isSuggested ? ghostIcon : destinationIcon;
                const marker = L.marker([destination.lat, destination.lng], { icon })
                    .addTo(mapRef.current!);

                // Security: DOM-based popup
                const popupDiv = document.createElement('div');
                popupDiv.style.minWidth = '120px';
                popupDiv.style.textAlign = 'center';

                const name = document.createElement('strong');
                name.textContent = destination.name;
                name.style.fontSize = '14px';
                popupDiv.appendChild(name);

                if (destination.isSuggested) {
                    const hint = document.createElement('div');
                    hint.textContent = 'Sugestie AI';
                    hint.style.fontSize = '11px';
                    hint.style.color = '#94a3b8';
                    hint.style.marginTop = '4px';
                    popupDiv.appendChild(hint);
                }

                marker.bindPopup(popupDiv);
                markerRef.current = marker;

                // Fly to destination
                mapRef.current!.flyTo([destination.lat, destination.lng], 6, {
                    duration: 1.5,
                });
            } catch (error) {
                // Silently fail - map will just not show marker
                logger.warn('Map marker error:', error);
            }
        } else {
            // Reset to default view (no destination or invalid coords)
            try {
                mapRef.current.flyTo([40, 10], 3, { duration: 1 });
            } catch {
                // Ignore fly errors
            }
        }
    }, [destination]);

    return (
        <div className="discovery-map-container">
            <div className="discovery-map" ref={mapContainerRef} />

            {!destination && (
                <div className="discovery-map-hint">
                    <svg className="hint-icon-svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                    <span>Spune-mi unde visezi să călătorești</span>
                </div>
            )}
        </div>
    );
}
